<template>
	<v-badge v-if="props.needAnchor" location="top right" color="primary" :offset-x="1" :offset-y="4" :modelValue="activeFilterChips.length > 0" dot dot-size="9">
		<v-btn v-if="props.needAnchor" ref="filtersButtonRef" icon="filter_alt" variant="text" color="primary" density="comfortable" rounded="xl" class="overflow-hidden" @click="openAndFocus" />
	</v-badge>

	<v-badge v-if="props.needSlide" location="top right" color="primary" :offset-x="1" :offset-y="5" :modelValue="activeFilterChips.length > 0" dot dot-size="9">
		<v-btn v-if="props.needSlide" variant="text" icon="filter_alt" color="primary" density="comfortable" rounded="xl" class="overflow-hidden" @click="activeDialog = !activeDialog"></v-btn>
	</v-badge>

	<DialogControl :attach="overlayTarget" :activator="activator" v-model="activeDialog" :transition="props.needSlide ? ComponentTransition : 'dialog-transition'" :class="{ 'dl-filters-overlay d-flex': props.needSlide }" eager>
		<v-card>
			<v-card-title class="headline">
				<v-btn v-if="props.needSlide" icon="arrow_back" variant="tonal" color="primary" rounded="xl" class="overflow-hidden" @click="activeDialog = false" />
				<v-icon v-else icon="filter_alt" variant="text" color="primary" rounded="xl" class="overflow-hidden" />
				<span class="text-primary ml-2">{{ t('Filters') }}</span>
				<v-spacer></v-spacer>
				<v-btn variant="text" rounded="xl" color="warning" @click="clearFilters" v-if="hasDirtyFilter">{{ t('Reset') }}</v-btn>
			</v-card-title>
			<v-card-text class="d-flex">
				<div class="dl-filters__perimeter">
					<TreeViewControl ref="deliveryLocationOrganisationTreeview" :label="t('Organisations')" :items="organisationTree" :title-key="'label'" :multiple="true" :branch-selection="true" :select-all="true" :show-selected-count="true" :expand-on-search="true" :search-fields="['title', 'reference']" items-registration="props" />
					<ToggleControl ref="deliveryLocationExcludeSharedToggle" :label="t('ExcludeSharedOrganisations')" />
				</div>
				<v-divider class="dl-filters__perimeter-divider" />

				<SelectControl ref="deliveryLocationAccountSelect" :label="t('Accounts')" :items="deliveryLocationAccounts" :multiple="true" />
				<SelectControl ref="deliveryLocationChannelSelect" :label="t('Channels')" :items="deliveryLocationChannels" :multiple="true" />
				<SelectControl ref="deliveryLocationTypeSelect" :label="t('DeliveryLocationType')" :items="deliveryLocationTypes" :multiple="true" />
				<SelectControl ref="deliveryLocationGroupSelect" :label="t('Groups')" :items="deliveryLocationGroups" :multiple="true" />
				<SelectControl ref="deliveryLocationUnreachedKpiSelect" :label="t('KpiNotReached')" :items="deliveryLocationKpis" :title-key="'reference'" :title-key-translate="true" :item-value="'_uid'" :multiple="true" />
				<SelectControl ref="deliveryLocationCanalSelect" :label="t('DistributionChannels')" :items="deliveryLocationCanals" :title-key="'label'" :multiple="true" />
				<SelectControl ref="deliveryLocationPosTypeSelect" :label="t('POSType')" :items="deliveryLocationPosTypes" :title-key="'label'" :multiple="true" />
				<SelectControl ref="deliveryLocationPostalCodeSelect" :label="t('Postcode')" :items="deliveryLocationPostalCodes" :multiple="true" />
				<SelectControl ref="deliveryLocationSeasonalitySelect" :label="t('DeliveryLocationSeasonality')" :items="deliveryLocationSeasonalities" :multiple="true" />
				<NumberControl ref="deliveryLocationFreshnessNumber" :label="t('Freshness')" v-if="hasSFA()" />

				<v-sheet class="ga-2" v-if="hasSFA()">
					<SelectControl ref="deliveryLocationOrderAssortSelect" :label="t('AssortOrder')" :items="orderAssortOptions" />
					<span class="text-body-2 ">{{ t('NoOrderAssortSince') }}</span>
					<v-menu  v-model="orderAssortDateMenu" :attach="overlayTarget || undefined" :close-on-content-click="false" transition="scale-transition">
						<template #activator="{ props: activatorProps }">
							<v-text-field :model-value="orderAssortDateLabel" readonly v-bind="activatorProps" density="compact" variant="outlined" hide-details prepend-inner-icon="event" />
						</template>
						<v-date-picker v-model="orderAssortDate" density="compact" color="primary" hide-header @update:model-value="orderAssortDateMenu = false"></v-date-picker>
					</v-menu>
				</v-sheet>

				<v-sheet class="ga-2" v-if="hasSFA()">
					<SelectControl ref="deliveryLocationCampaignSelect" :label="t('Campaign')" :items="deliveryLocationCampaigns" :title-key="'label'" />
					<ToggleControl ref="deliveryLocationOnlyMissingToggle" :label="t('lblOnlyMissingPlanningPromo')" />
					<v-btn variant="tonal" color="secondary" @click="gotoPromoHandler">{{ t('PromoPlanning') }}</v-btn>
				</v-sheet>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text color="error" @click="activeDialog = false">{{ t('Close') }}</v-btn>
				<v-btn variant="tonal" @click="saveFilters">{{ t('Save') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
// #region Imports

import { ref, onMounted, onBeforeUnmount, inject, computed, watch, nextTick } from 'vue';
import { eventBus } from '@/utils/core/eventBus.js';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { hasSFA } from '@/utils/legacy/processUtils.js';
import { hydrateLocationsKpis } from '@/utils/domain/appointmentUtils.js';
import { buildFilterChips } from '@/utils/ui/filterChips.js';
import { kpiService } from '@/services/kpiService';
import { getDeliveryLocationSeasonalities } from '@/api/sales/deliveryLocationSeasonality';
import { getCampaigns } from '@/api/core/campaign';
import { getOrganisationsTree } from '@/api/core/organisation';
import { getOrganisationSharings } from '@/api/core/organisationSharing';
import { buildOrganisationById, buildOrgSharingExpansionMap, buildUserOrgStrictScope, expandOrganisationPerimeter, isDeliveryLocationShared } from '@/utils/domain/organisationScope';
import SelectControl from '@/components/controls/SelectControl.vue';
import NumberControl from '@/components/controls/NumberControl.vue';
import ToggleControl from '@/components/controls/ToggleControl.vue';
import TreeViewControl from '@/components/controls/TreeViewControl.vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import { createCssTransition } from 'vuetify/util/transitions';
import { useFilterStore, emptyFilters } from '@/stores/FilterStore';

const ComponentTransition = createCssTransition('slideX-rtl-transition');
const show = ref(false);
// #endregion

// #region Refs & Vars
const emit = defineEmits(['filters-changed', 'focus']);
const props = defineProps({
	activator: { type: [Object, String], default: null },
	locations: { type: Array, default: () => [] },
	needAnchor: { type: Boolean, default: false },
	needSlide: { type: Boolean, default: false },
	needSfaFilter: { type: Boolean, default: false },
	needLegacyBroadcast: { type: Boolean, default: false },
	useViewPersistence: { type: Boolean, default: false },
});

const t = useTranslator();
const filterStore = useFilterStore();
const activeDialog = ref(false);
const overlayTarget = inject('overlayTarget', null);
const originalLocations = ref([]);
const ecosVM = iframeDataBridge.getEcosVM();
const deliveryLocations = ref(ecosVM?.deliveryLocations ?? []);
const sfaHelper = iframeDataBridge.getSfaHelper();
const dateHelper = iframeDataBridge.getDateHelper();
const hasSFAProcess = hasSFA();
const salesHelper = iframeDataBridge.getSalesHelper();

let kpiExcluded = ['OrderAssortWeight', 'OrderAssortValue', 'OrderAssortWeightDelivered', 'OrderAssortValueDelivered'];

const deliveryLocationKpis = hasSFAProcess ? kpiService.getFilterableKpis().filter((k) => k && k.reference && !kpiExcluded.includes(k.reference)) : [];
const deliveryLocationTypeSelect = ref(null);
const deliveryLocationAccountSelect = ref(null);
const deliveryLocationChannelSelect = ref(null);
const deliveryLocationGroupSelect = ref(null);
const deliveryLocationCanalSelect = ref(null);
const deliveryLocationPosTypeSelect = ref(null);
const deliveryLocationPostalCodeSelect = ref(null);
const deliveryLocationFreshnessNumber = ref(null);
const deliveryLocationUnreachedKpiSelect = ref(null);
const deliveryLocationSeasonalitySelect = ref(null);
const seasonalitiesAll = ref([]);
const deliveryLocationOrderAssortSelect = ref(null);
const orderAssortDate = ref(null);
const orderAssortDateMenu = ref(false);
const orderAssortOptions = [
	{ id: '0', name: t('NoOrderAssort') },
	{ id: '1', name: t('OrderedAssort') },
];
const deliveryLocationCampaignSelect = ref(null);
const deliveryLocationOnlyMissingToggle = ref(null);
const campaignsAll = ref([]);
const deliveryLocationOrganisationTreeview = ref(null);
const deliveryLocationExcludeSharedToggle = ref(null);
const organisationTree = ref([]);

const filterSelectRefs = {
	organisations: deliveryLocationOrganisationTreeview,
	type: deliveryLocationTypeSelect,
	account: deliveryLocationAccountSelect,
	channel: deliveryLocationChannelSelect,
	group: deliveryLocationGroupSelect,
	canal: deliveryLocationCanalSelect,
	posType: deliveryLocationPosTypeSelect,
	postalCode: deliveryLocationPostalCodeSelect,
	kpi: deliveryLocationUnreachedKpiSelect,
	seasonality: deliveryLocationSeasonalitySelect,
};
let isPushingToStore = false;

function openAndFocus(event) {
	emit('focus', event);
	activeDialog.value = true;
}

// #endregion

// #region Computed

// same row rendering as the legacy DataTable's fnCreatedCell (salesHelper.normalizeDeliveryLocation)
const hasDirtyFilter = computed(() => {
	return getFiltersDirtyState();
});

// snapshot of the ids actually applied to the list (populated by applyFilters), used to render removable v-chips
const appliedFilters = ref(emptyFilters());

// one aggregated chip per filter category (see src/utils/ui/filterChips.js — same shared shape/component
// as KpiBuilder/SpsBuilder's "active filters" row), built from the ids snapshotted by applyFilters()
function filterValuesFor(list, ids, itemValue = 'id') {
	const idSet = new Set(ids || []);
	return (list || []).filter((item) => idSet.has(item[itemValue]));
}

const organisationFlatList = computed(() => {
	const flatten = (nodes) => nodes.flatMap((node) => [node, ...(node.children ? flatten(node.children) : [])]);
	return flatten(organisationTree.value);
});

const activeFilterChips = computed(() =>
	buildFilterChips([
		{ key: 'organisations', label: t('Organisations'), values: filterValuesFor(organisationFlatList.value, appliedFilters.value.organisations), valueKey: 'label' },
		{ key: 'excludeSharedOrganisations', label: t('ExcludeSharedOrganisations'), active: appliedFilters.value.excludeSharedOrganisations === true },
		{ key: 'type', label: t('DeliveryLocationType'), values: filterValuesFor(deliveryLocationTypes.value, appliedFilters.value.type), valueKey: 'name' },
		{ key: 'account', label: t('Accounts'), values: filterValuesFor(deliveryLocationAccounts.value, appliedFilters.value.account), valueKey: 'name' },
		{ key: 'channel', label: t('Channels'), values: filterValuesFor(deliveryLocationChannels.value, appliedFilters.value.channel), valueKey: 'name' },
		{ key: 'group', label: t('Groups'), values: filterValuesFor(deliveryLocationGroups.value, appliedFilters.value.group), valueKey: 'name' },
		{ key: 'canal', label: t('DistributionChannels'), values: filterValuesFor(deliveryLocationCanals.value, appliedFilters.value.canal), valueKey: 'label' },
		{ key: 'posType', label: t('POSType'), values: filterValuesFor(deliveryLocationPosTypes.value, appliedFilters.value.posType), valueKey: 'label' },
		{ key: 'postalCode', label: t('Postcode'), values: filterValuesFor(deliveryLocationPostalCodes.value, appliedFilters.value.postalCode), valueKey: 'name' },
		{ key: 'kpi', label: t('KpiNotReached'), values: filterValuesFor(deliveryLocationKpis, appliedFilters.value.kpi, '_uid').map((kpi) => ({ ...kpi, reference: t(kpi.reference) })), valueKey: 'reference' },
		{ key: 'freshness', label: t('Freshness'), values: appliedFilters.value.freshness > 0 ? [appliedFilters.value.freshness] : [], operator: '>' },
		{ key: 'seasonality', label: t('DeliveryLocationSeasonality'), values: filterValuesFor(deliveryLocationSeasonalities.value, appliedFilters.value.seasonality), valueKey: 'name' },
		{ key: 'orderAssortMode', label: t('AssortOrder'), values: filterValuesFor(orderAssortOptions, appliedFilters.value.orderAssortMode ? [appliedFilters.value.orderAssortMode] : [], 'id'), valueKey: 'name' },
		{ key: 'orderAssortDate', label: t('NoOrderAssortSince'), values: appliedFilters.value.orderAssortDate ? [new Date(appliedFilters.value.orderAssortDate).toLocaleDateString()] : [] },
		{ key: 'campaign', label: t('Campaign'), values: filterValuesFor(deliveryLocationCampaigns.value, appliedFilters.value.campaign ? [appliedFilters.value.campaign] : []), valueKey: 'label' },
		{ key: 'onlyMissing', label: t('lblOnlyMissingPlanningPromo'), active: appliedFilters.value.onlyMissing === true },
	])
);

const deliveryLocationTypes = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.deliveryLocationTypeId));
	return ecosVM.deliveryLocationTypes.filter((dltype) => setIds.has(dltype.id)) || [];
});

const deliveryLocationAccounts = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.accountId));
	return ecosVM.accounts().filter((account) => setIds.has(account.id)) || [];
});

const deliveryLocationChannels = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.channelId));
	return ecosVM.channels.filter((channel) => setIds.has(channel.id)) || [];
});

const deliveryLocationGroups = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.groupId));
	return ecosVM.DLGroups.filter((group) => setIds.has(group.id)) || [];
});

const deliveryLocationCanals = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.distributionChannel));
	return ecosVM.distributionChannels.filter((dlCanal) => setIds.has(dlCanal.id)) || [];
});

const deliveryLocationPosTypes = computed(() => {
	const setIds = new Set(deliveryLocations.value.map((e) => e.POSTypeId));
	return ecosVM.posTypes.filter((posType) => setIds.has(posType.id)) || [];
});

const deliveryLocationPostalCodes = computed(() =>
	props.locations
		.map((dl) => getPostalCodePrefix(dl.postcode))
		.filter((prefix, index, allPrefixes) => prefix && allPrefixes.indexOf(prefix) === index)
		.sort((a, b) => a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' }))
		.map((prefix) => ({ id: prefix, name: prefix }))
);

const deliveryLocationSeasonalities = computed(() => {
	const setIds = new Set(deliveryLocations.value.flatMap((e) => e.seasonalities || []));
	return seasonalitiesAll.value.filter((s) => setIds.has(s.id));
});

const deliveryLocationCampaigns = computed(() => campaignsAll.value);

// v-date-picker returns a Date object, no time part
const orderAssortDateLabel = computed(() => (orderAssortDate.value ? new Date(orderAssortDate.value).toLocaleDateString() : ''));

//#endregion

// #region Lifecycle Hooks

onMounted(() => {
	originalLocations.value = Array.from(props.locations);
	getDeliveryLocationSeasonalities().then((list) => (seasonalitiesAll.value = list));
	getOrganisationsTree().then((tree) => (organisationTree.value = tree));
	if (hasSFAProcess) getCampaigns().then((list) => (campaignsAll.value = list));

	if (originalLocations.value.length > 0 && !props.useViewPersistence) {
		computeAndEmitFilters(restoreFiltersFromStore(), originalLocations.value);
	}

	eventBus.on('vue-persistent-zone-replaced', onZoneReattached);
	eventBus.on('delivery-location-created', onDeliveryLocationCreated);
});

onBeforeUnmount(() => {
	eventBus.off('vue-persistent-zone-replaced', onZoneReattached);
	eventBus.off('delivery-location-created', onDeliveryLocationCreated);
});

// #endregion

// #region Watchers

watch(
	() => props.locations,
	(newLocations) => {
		const wasEmpty = originalLocations.value.length === 0;
		if (!getFiltersDirtyState()) {
			originalLocations.value = Array.from(newLocations || []);
		}
		if (wasEmpty && originalLocations.value.length > 0 && !props.useViewPersistence && filterStore.hasFilters) {
			computeAndEmitFilters(restoreFiltersFromStore(), originalLocations.value);
		}
	}
);

watch(
	() => filterStore.filters,
	async () => {
		if (isPushingToStore || props.useViewPersistence) return;
		await computeAndEmitFilters(restoreFiltersFromStore(), originalLocations.value);
	},
	{ deep: true }
);

// #endregion

// #region Methods

function restoreFiltersFromStore() {
	return applyFilterState(filterStore.filters);
}

function applyFilterState(state) {
	const saved = { ...emptyFilters(), ...(state || {}) };

	deliveryLocationOrganisationTreeview.value.selectItems(saved.organisations);
	deliveryLocationExcludeSharedToggle.value.selectItems(saved.excludeSharedOrganisations);
	deliveryLocationTypeSelect.value.selectItems(saved.type);
	deliveryLocationAccountSelect.value.selectItems(saved.account);
	deliveryLocationChannelSelect.value.selectItems(saved.channel);
	deliveryLocationGroupSelect.value.selectItems(saved.group);
	deliveryLocationCanalSelect.value.selectItems(saved.canal);
	deliveryLocationPosTypeSelect.value.selectItems(saved.posType);
	deliveryLocationPostalCodeSelect.value.selectItems(saved.postalCode);
	deliveryLocationSeasonalitySelect.value.selectItems(saved.seasonality);

	if (hasSFAProcess) {
		deliveryLocationFreshnessNumber.value.selectItems(saved.freshness || null);
		deliveryLocationUnreachedKpiSelect.value.selectItems(saved.kpi);
		deliveryLocationOrderAssortSelect.value.selectItems(saved.orderAssortMode);
		orderAssortDate.value = saved.orderAssortDate;
		deliveryLocationCampaignSelect.value.selectItems(saved.campaign);
		deliveryLocationOnlyMissingToggle.value.selectItems(saved.onlyMissing);
	}

	return readSelectedFilters();
}

function readSelectedFilters() {
	return {
		organisations: deliveryLocationOrganisationTreeview.value.getValues() || [],
		excludeSharedOrganisations: deliveryLocationExcludeSharedToggle.value.getValues() === true,
		type: deliveryLocationTypeSelect.value.getValues() || [],
		account: deliveryLocationAccountSelect.value.getValues() || [],
		channel: deliveryLocationChannelSelect.value.getValues() || [],
		group: deliveryLocationGroupSelect.value.getValues() || [],
		canal: deliveryLocationCanalSelect.value.getValues() || [],
		posType: deliveryLocationPosTypeSelect.value.getValues() || [],
		postalCode: deliveryLocationPostalCodeSelect.value.getValues() || [],
		freshness: hasSFAProcess ? Number(deliveryLocationFreshnessNumber.value.getValue() || 0) : 0,
		kpi: hasSFAProcess ? deliveryLocationUnreachedKpiSelect.value.getValues() || [] : [],
		seasonality: deliveryLocationSeasonalitySelect.value.getValues() || [],
		orderAssortMode: hasSFAProcess ? deliveryLocationOrderAssortSelect.value.getValues() : null,
		orderAssortDate: orderAssortDate.value,
		campaign: hasSFAProcess ? deliveryLocationCampaignSelect.value.getValues() : null,
		onlyMissing: hasSFAProcess ? deliveryLocationOnlyMissingToggle.value.getValues() === true : false,
	};
}

async function applyFilters(locations = null) {
	locations = Array.isArray(locations) ? locations : originalLocations.value;
	originalLocations.value = Array.from(locations);

	await computeAndEmitFilters(readSelectedFilters(), locations);

	if (props.useViewPersistence) return;

	isPushingToStore = true;
	filterStore.setFilters(appliedFilters.value);
	await nextTick();
	isPushingToStore = false;
}

async function saveFilters() {
	activeDialog.value = false;
	iframeDataBridge.getIframeWindow().externis.greeterVM.startLoading();
	await applyFilters();
	requestAnimationFrame(iframeDataBridge.getIframeWindow().externis.greeterVM.stopLoading());
}

async function computeAndEmitFilters(filters, locations) {
	// Get KPIs data for KPI filtering
	if (hasSFAProcess && filters.kpi.length > 0) {
		await hydrateLocationsKpis(locations, filters.kpi);
	}

	// // Direct order filter: ask legacy salesHelper which locations have an assort order since orderAssortDate
	// let orderAssortValidIds = null;
	// if (hasSFAProcess && filters.orderAssortMode !== null && orderAssortDate.value) {
	// 	orderAssortValidIds = await new Promise((resolve) =>
	// 		salesHelper.assort.getAssortOrderSince(
	// 			locations.map((dl) => dl.id),
	// 			new Date(orderAssortDate.value),
	// 			resolve,
	// 			iframeDataBridge.getCurrentUser()?.organisationId
	// 		)
	// 	);
	// }

	// Campaign filter: get the campaign's delivery location scope from legacy sfaHelper
	let campaignDlIds = null;
	if (hasSFAProcess && filters.campaign) {
		const tt = await new Promise((resolve) => sfaHelper.campaign.getCampaignTT({ cid: filters.campaign }, resolve));
		campaignDlIds = (tt?.dlList || []).map((dl) => dl.id);
	}

	const today = new Date();
	const legacyLocations = filters.freshness > 0 ? new Map((iframeDataBridge.getEcosVM()?.deliveryLocations || []).map((dl) => [dl.id, dl])) : null;

	const selectedOrganisations = filters.organisations || [];
	const excludeSharedOrganisations = filters.excludeSharedOrganisations === true;
	let organisationIdsByLocationId = null;
	let expandedOrgIds = null;
	let userOrgStrictScope = null;

	if (excludeSharedOrganisations || selectedOrganisations.length > 0) {
		const currentEcosVM = iframeDataBridge.getEcosVM();
		const organisations = typeof currentEcosVM?.organisations === 'function' ? currentEcosVM.organisations() || [] : currentEcosVM?.organisations || [];
		const organisationById = buildOrganisationById(organisations);
		organisationIdsByLocationId = new Map((currentEcosVM?.deliveryLocations || []).map((dl) => [dl.id, Array.isArray(dl.organisations) ? dl.organisations.map(Number) : []]));

		if (excludeSharedOrganisations) {
			userOrgStrictScope = buildUserOrgStrictScope(Number(iframeDataBridge.getContext()?.user?.organisationId || 0), organisationById);
		}
		if (selectedOrganisations.length > 0) {
			expandedOrgIds = expandOrganisationPerimeter(selectedOrganisations, organisationById, buildOrgSharingExpansionMap(await getOrganisationSharings()), excludeSharedOrganisations, false);
		}
	}

	let filteredLocations = locations.filter((location) => {
		const locationOrganisations = organisationIdsByLocationId?.get(location.id) || [];
		const matchesExcludeShared = !userOrgStrictScope || !isDeliveryLocationShared(locationOrganisations, userOrgStrictScope);
		const matchesOrganisation = !expandedOrgIds || locationOrganisations.some((organisationId) => expandedOrgIds.has(organisationId));

		const freshnessLocation = legacyLocations?.get(location.id) || location;
		const lastAppointment = freshnessLocation._lastAppointmentWithStatement;
		const hasFutureAppointment = !!freshnessLocation.nextAppointmentDate && !dateHelper.isDateLess(freshnessLocation.nextAppointmentDate, today);
		const isTooOld = !lastAppointment || Math.abs(dateHelper.getDiffDays(lastAppointment.date, today)) >= filters.freshness;

		const matchesType = filters.type.length === 0 || filters.type.includes(location.deliveryLocationTypeId);
		const matchesAccount = filters.account.length === 0 || filters.account.includes(location.accountId);
		const matchesChannel = filters.channel.length === 0 || filters.channel.includes(location.channelId);
		const matchesGroup = filters.group.length === 0 || filters.group.includes(location.groupId);
		const matchesCanal = filters.canal.length === 0 || filters.canal.includes(location.distributionChannel);
		const matchesPosType = filters.posType.length === 0 || filters.posType.includes(location.POSTypeId);
		const matchesPostalCode = filters.postalCode.length === 0 || filters.postalCode.includes(getPostalCodePrefix(location.postcode));
		const matchesFreshness = filters.freshness <= 0 || (isTooOld && !hasFutureAppointment);
		const matchesKpi = filters.kpi.length === 0 || sfaHelper.kpi.evaluateKpis(location, Array.from(filters.kpi));
		const matchesSeasonality = filters.seasonality.length === 0 || (location.seasonalities || []).some((id) => filters.seasonality.includes(id));
		// const matchesOrderAssort = !orderAssortValidIds || (filters.orderAssortMode === '0' ? !orderAssortValidIds.includes(location.id) : orderAssortValidIds.includes(location.id));
		// Simplification: legacy recomputes "planned" from appointments scheduled within the campaign's own nego/store period (vwSFAPlanning.js#getTT).
		// Here we reuse the location's already-hydrated nextAppointmentDate as a stand-in for "has a planned visit", instead of a period-aware appointment lookup.
		const matchesCampaign = !campaignDlIds || (campaignDlIds.includes(location.id) && (!filters.onlyMissing || !location.nextAppointmentDate));

		return matchesExcludeShared && matchesOrganisation && matchesType && matchesAccount && matchesChannel && matchesGroup && matchesCanal && matchesPosType && matchesPostalCode && matchesFreshness && matchesKpi && matchesSeasonality && matchesCampaign;
	});

	// Filter Order moved to call getAssortOrderSince with less deliveryLocations
	// Direct order filter: ask legacy salesHelper which locations have an assort order since orderAssortDate
	let orderAssortValidIds = null;
	if (hasSFAProcess && filters.orderAssortMode !== null && orderAssortDate.value) {
		orderAssortValidIds = await new Promise((resolve) =>
			salesHelper.assort.getAssortOrderSince(
				filteredLocations.map((dl) => dl.id),
				new Date(orderAssortDate.value),
				resolve,
				iframeDataBridge.getCurrentUser()?.organisationId
			)
		);
	}

	filteredLocations = filteredLocations.filter((location) => {
		const matchesOrderAssort = !orderAssortValidIds || (filters.orderAssortMode === '0' ? !orderAssortValidIds.includes(location.id) : orderAssortValidIds.includes(location.id));
		return matchesOrderAssort;
	});

	//&& matchesOrderAssort

	appliedFilters.value = filters;

	emitDeliveryLocationFilterResult(filteredLocations);
	emit('filters-changed', { filteredLocations, dirty: getFiltersDirtyState(), chips: activeFilterChips.value });
}

// clears every filter and re-applies immediately (called from DeliveryLocationDialog's FilterChips @clear)
function clearFilters() {
	resetFilters();
	applyFilters();
}

function emitDeliveryLocationFilterResult(filtered) {
	if (!props.needLegacyBroadcast) return;
	amplify?.publish('evtDeliveryLocationFiltersChanged', {
		filteredLocations: Array.isArray(filtered) ? filtered : [],
	});
}

function getPostalCodePrefix(postcode) {
	const normalizedPostcode = String(postcode ?? '').trim();
	return normalizedPostcode ? normalizedPostcode.substring(0, 2).toUpperCase() : null;
}

function getFiltersDirtyState() {
	try {
		let dirty =
			deliveryLocationOrganisationTreeview.value.isDirty() ||
			deliveryLocationExcludeSharedToggle.value.isDirty() ||
			deliveryLocationTypeSelect.value.isDirty() ||
			deliveryLocationAccountSelect.value.isDirty() ||
			deliveryLocationChannelSelect.value.isDirty() ||
			deliveryLocationGroupSelect.value.isDirty() ||
			deliveryLocationCanalSelect.value.isDirty() ||
			deliveryLocationPosTypeSelect.value.isDirty() ||
			deliveryLocationPostalCodeSelect.value.isDirty() ||
			deliveryLocationSeasonalitySelect.value.isDirty();
		if (hasSFAProcess) dirty = dirty || deliveryLocationFreshnessNumber.value.isDirty() || deliveryLocationUnreachedKpiSelect.value.isDirty();
		if (hasSFAProcess) dirty = dirty || deliveryLocationOrderAssortSelect.value.isDirty() || !!orderAssortDate.value || deliveryLocationCampaignSelect.value.isDirty() || deliveryLocationOnlyMissingToggle.value.isDirty();
		return dirty;
	} catch {
		return false;
	}
}

// navigates to the legacy promo planning view (same postMessage/amplify contract as vwSFAPlanning.js#gotoPromoHandler)
function gotoPromoHandler() {
	amplify?.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vwSFAPromo' });
}

// Zone got reattached to a new page (KO re-render) — re-apply already-selected filters
// so the new page's list is filtered immediately, without reopening the dialog.
function onZoneReattached({ selector }) {
	if (selector === 'vue-delivery-location-filter-dialog-zone' && activeFilterChips.value.length) {
		applyFilters(originalLocations.value);
	}
}

function onDeliveryLocationCreated() {
	deliveryLocations.value = iframeDataBridge.getEcosVM()?.deliveryLocations ?? [];
}

function open() {
	activeDialog.value = true;
}

function originalLocationsUpdated(newLocations) {
	originalLocations.value = Array.from(newLocations);
}

// clears one filter category and re-applies the filtering (called from DeliveryLocationDialog's FilterChips @remove)
function removeFilterValue(key) {
	if (key === 'freshness') {
		deliveryLocationFreshnessNumber.value.selectItems(null);
	} else if (key === 'orderAssortMode') {
		deliveryLocationOrderAssortSelect.value.selectItems(null);
	} else if (key === 'orderAssortDate') {
		orderAssortDate.value = null;
	} else if (key === 'onlyMissing') {
		deliveryLocationOnlyMissingToggle.value.selectItems(false);
	} else if (key === 'campaign') {
		deliveryLocationCampaignSelect.value.selectItems(null);
	} else if (key === 'excludeSharedOrganisations') {
		deliveryLocationExcludeSharedToggle.value.selectItems(false);
	} else {
		filterSelectRefs[key]?.value?.selectItems([]);
	}
	applyFilters();
}

function resetFilters() {
	deliveryLocationOrganisationTreeview.value.selectItems([]);
	deliveryLocationExcludeSharedToggle.value.selectItems(false);
	deliveryLocationTypeSelect.value.selectItems([]);
	deliveryLocationAccountSelect.value.selectItems([]);
	deliveryLocationChannelSelect.value.selectItems([]);
	deliveryLocationGroupSelect.value.selectItems([]);
	deliveryLocationCanalSelect.value.selectItems([]);
	deliveryLocationPosTypeSelect.value.selectItems([]);
	deliveryLocationPostalCodeSelect.value.selectItems([]);
	deliveryLocationSeasonalitySelect.value.selectItems([]);

	if (hasSFAProcess) {
		deliveryLocationFreshnessNumber.value.selectItems(null);
		deliveryLocationUnreachedKpiSelect.value.selectItems([]);
		deliveryLocationOrderAssortSelect.value.selectItems(null);
		orderAssortDate.value = null;
		deliveryLocationCampaignSelect.value.selectItems(null);
		deliveryLocationOnlyMissingToggle.value.selectItems(false);
	}

	if (!props.useViewPersistence) filterStore.resetFilters();
}

// #endregion

defineExpose({
	originalLocationsUpdated,
	applyFilters,
	removeFilterValue,
	clearFilters,
	open,
	getFilterState: readSelectedFilters,
	applyFilterState,
});
</script>

<style lang="scss">
.dl-filters__perimeter {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.dl-filters__perimeter-divider {
	margin-bottom: 16px;
}

.dl-filters-overlay {
	max-width: 600px !important;
	overflow-x: hidden;

	.v-card-title {
		background-color: rgb(var(--v-theme-surface-variant)) !important;
	}

	.v-overlay__content {
		width: 100% !important;
		height: 100%;
		max-width: none;
	}

	.v-overlay__scrim {
		background-color: #00000000 !important; // transparent
	}

	.v-card {
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow-y: auto;
	}
}
</style>
